import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  Card,
  CurrencyValue,
  DisplaySwitch,
  formatNumber,
  KpiCard,
  KpiSkeleton,
  LineChart,
  useIsMobile
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';
import RevenueBreakdown from './RevenueBreakdown';

type DataResponse = {
  date: string;
  aum_usd: string;
  aum_usd_change: string;
  profit_estimated_usd: string;
  profit_estimated_usd_change: string;
  profit_total: string;
  profit_total_change: string;
  real_profit_estimated_usd: string;
  real_profit_estimated_usd_change: string;
  historic: SimpleChartData[];
};

export default function RevenueTillNow({ network }: { network: string }) {
  const color = useSparkColor();
  const isMobile = useIsMobile();

  const [chartDaysAgo, setChartDaysAgo] = useState('90');
  const [activeDisplayOption, setActiveDisplayOption] = useState('historical');

  const [data, error] = useApi<DataResponse>({
    url: `/sparkstar/sll/networks/${network}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasetsProfitTotal = [
    {
      xField: 'date',
      yField: 'profit_total',
      borderColor: color
    }
  ];

  const chartDataTotal = useChartFillDatasetsDates(
    data?.historic,
    datasetsProfitTotal,
    chartDaysAgo
  );

  const displayOptions = useMemo(
    () => [
      { key: 'historical', value: 'Historical' },
      { key: 'breakdown', value: 'Breakdown' }
    ],
    []
  );
  const valueFormatter = useCallback((value: string | number) => {
    return <CurrencyValue value={value} variant="dynamic" />;
  }, []);

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 2.5
      }
    }),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  return (
    <ExpandableKpiCard
      text="Total Revenue Until Now"
      defaultShowChart={!isMobile}
      hoverBorderColor={color}
      justChart={false}
      expandText="details"
      kpiCard={
        <KpiCard
          data={{
            profit_total: data['profit_total'],
            profit_total_change: data['profit_total_change']
          }}
          field="profit_total"
          options={{
            currency: 'USD',
            notation: 'compact'
          }}
          variant="nestedAsChild"
        />
      }>
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      {activeDisplayOption === 'breakdown' && (
        <RevenueBreakdown
          showChart={false}
          field="profit_total_usd"
          network={network}
        />
      )}
      {activeDisplayOption === 'historical' && (
        <Card backgroundColor="foreground">
          <LineChart
            data={chartDataTotal}
            datasets={datasetsProfitTotal}
            valueFormatter={valueFormatter}
            config={customConfig}
            showValuesInTooltip={true}
            tooltip={{
              valueFormatter: (value) => {
                return formatNumber(Number(value));
              },
              labelFormatter: () => 'Total',
              displayColors: false
            }}
            timeFilter={
              <ChartTimeFilter
                activeOption={chartDaysAgo}
                onChange={setChartDaysAgo}
              />
            }
          />
        </Card>
      )}
    </ExpandableKpiCard>
  );
}
